import React, { useState } from "react"
import { Link } from "react-router-dom"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"
import { Tabs, TabsList, TabsTrigger } from "src/components/ui/tabs"
import { cn } from "src/utils/cn"
import { formatINR } from "../constants"

export default function AgingTable({ byProject = [], byCustomer = [] }) {
  const [view, setView] = useState("project")

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <Tabs value={view} onValueChange={setView}>
        <TabsList className="flex justify-start gap-1 rounded-none border-b border-slate-200 bg-transparent px-4 p-0">
          <ViewTab value="project" active={view === "project"}>By Project</ViewTab>
          <ViewTab value="customer" active={view === "customer"}>By Customer</ViewTab>
        </TabsList>
      </Tabs>

      {view === "project" ? (
        <ProjectView rows={byProject} />
      ) : (
        <CustomerView rows={byCustomer} />
      )}
    </div>
  )
}

function ProjectView({ rows }) {
  if (!rows.length) {
    return <EmptyRow message="No outstanding installments by project." />
  }

  return (
    <Table className="text-[13px]">
      <TableHeader className="bg-slate-50 text-left text-slate-500">
        <TableRow className="hover:bg-transparent">
          <TableHead className="h-9 font-medium text-slate-500">Project</TableHead>
          <TableHead className="h-9 text-right font-medium text-slate-500">Current</TableHead>
          <TableHead className="h-9 text-right font-medium text-slate-500">0–30d</TableHead>
          <TableHead className="h-9 text-right font-medium text-slate-500">31–60d</TableHead>
          <TableHead className="h-9 text-right font-medium text-slate-500">61–90d</TableHead>
          <TableHead className="h-9 text-right font-medium text-slate-500">90+d</TableHead>
          <TableHead className="h-9 text-right font-medium text-slate-500">Total</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.project.id}>
            <TableCell className="py-2.5 font-medium text-slate-900">{row.project.name}</TableCell>
            <TableCell className="py-2.5 text-right text-slate-600">{formatINR(row.current)}</TableCell>
            <TableCell className="py-2.5 text-right text-emerald-600">{formatINR(row.b0_30)}</TableCell>
            <TableCell className="py-2.5 text-right text-amber-600">{formatINR(row.b31_60)}</TableCell>
            <TableCell className="py-2.5 text-right text-orange-600">{formatINR(row.b61_90)}</TableCell>
            <TableCell className="py-2.5 text-right text-red-600">{formatINR(row.b90_plus)}</TableCell>
            <TableCell className="py-2.5 text-right font-medium text-slate-900">{formatINR(row.total)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function CustomerView({ rows }) {
  if (!rows.length) {
    return <EmptyRow message="No overdue customers." />
  }

  return (
    <Table className="text-[13px]">
      <TableHeader className="bg-slate-50 text-left text-slate-500">
        <TableRow className="hover:bg-transparent">
          <TableHead className="h-9 font-medium text-slate-500">Customer</TableHead>
          <TableHead className="h-9 text-right font-medium text-slate-500">Overdue Amount</TableHead>
          <TableHead className="h-9 text-right font-medium text-slate-500">Oldest (days)</TableHead>
          <TableHead className="h-9 font-medium text-slate-500">Booking</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i}>
            <TableCell className="py-2.5 font-medium text-slate-900">{row.customer_name}</TableCell>
            <TableCell className="py-2.5 text-right text-red-600 font-medium">
              {formatINR(row.total_overdue)}
            </TableCell>
            <TableCell className="py-2.5 text-right text-slate-600">{row.oldest_days}d</TableCell>
            <TableCell className="py-2.5">
              <Link
                to={`/bookings/${row.booking_id}`}
                className="text-blue-600 hover:underline"
              >
                View
              </Link>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function ViewTab({ value, active, children }) {
  return (
    <TabsTrigger
      value={value}
      className={cn(
        "-mb-px rounded-none border-b-2 bg-transparent px-3 py-3 text-[13px] font-medium shadow-none transition-colors",
        active
          ? "border-slate-900 text-slate-900"
          : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700"
      )}
    >
      {children}
    </TabsTrigger>
  )
}

function EmptyRow({ message }) {
  return (
    <div className="px-6 py-10 text-center text-[13px] text-slate-400">
      {message}
    </div>
  )
}
